/**
 * Excluir anúncio.
 *
 * The BFF soft-deletes (`deletedAt` + `isActive: false`) and accepts any status, but exposes no
 * restore endpoint — so from this panel the removal is one-way, and the dialog says exactly that
 * instead of implying a trash bin that does not exist.
 *
 * An approved announcement is already being shown inside the tenant's platform, which is a
 * different weight of decision from dropping a draft. The dialog calls that out.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { WhatsNew } from '../../Types/WhatsNew.types';
import { WhatsNewApprovalStatus } from '../../Types/WhatsNew.types';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const textStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const titleStyle = css`
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const metaStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const alertStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  border: 1px solid var(--color-warning);
`;

const alertTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

const alertTextStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface DeleteWhatsNewModalProps {
  open: boolean;
  item: WhatsNew;
  isDeleting: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (item: WhatsNew) => void;
}

export const DeleteWhatsNewModal = ({
  open,
  item,
  isDeleting,
  error,
  onClose,
  onConfirm,
}: DeleteWhatsNewModalProps): JSX.Element => {
  const isApproved = item.approvalStatus === WhatsNewApprovalStatus.APPROVED;

  const content = (
    <div className={bodyStyle}>
      <p className={textStyle}>
        O anúncio <span className={titleStyle}>{item.welcomeMessage}</span> sai da listagem e deixa
        de ser entregue. O painel não tem como trazê-lo de volta.
      </p>

      <span className={metaStyle}>
        {item.tenantSlug} · {item.page?.slug ?? 'sem página'} · v{item.platformVersion?.version}
      </span>

      {isApproved ? (
        <div className={alertStyle}>
          <span className={alertTitleStyle}>Este anúncio está aprovado</span>
          <span className={alertTextStyle}>
            Ele já aparece dentro da plataforma do tenant. Quem ainda não viu não verá mais, e o
            registro de quem viu continua guardado.
          </span>
        </div>
      ) : null}

      {error !== null ? <span className={errorStyle}>{error}</span> : null}

      <div className={footerStyle}>
        <UTButton fitContent variant='secondary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='whats-new-delete-confirm'
          variant='primary'
          text={isDeleting ? 'Excluindo...' : 'Excluir anúncio'}
          disabled={isDeleting}
          onClick={() => {
            onConfirm(item);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title='Excluir anúncio'
      content={content}
      maxWidth='500px'
      hasButtonClose
      position='center'
    />
  );
};
